import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, Card, Money } from '@clari/ui'
import { ErrorBanner } from '@/components/ErrorBanner'
import { AccountSkeleton } from '@/components/banking/skeletons'
import { apiErrorMessage } from '@/lib/api-error'
import { bankingApi } from '@/lib/banking-api'
import type { AccountDto } from '@/lib/types'

export function Account() {
  const { t, i18n } = useTranslation('banking')
  const queryClient = useQueryClient()
  const q = useQuery({ queryKey: ['accounts'], queryFn: () => bankingApi.listAccounts() })
  const accounts = q.data?.items ?? []
  const [error, setError] = useState<string | null>(null)

  const addMut = useMutation({
    mutationFn: () => bankingApi.addIban(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['accounts'] }),
    onError: (err) => setError(apiErrorMessage(err)),
  })
  const preferMut = useMutation({
    mutationFn: (id: string) => bankingApi.setPreferredIban(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['accounts'] }),
    onError: (err) => setError(apiErrorMessage(err)),
  })

  return (
    <div className="mx-auto flex max-w-xl flex-col gap-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">{t('account.title')}</h1>
        <Button
          variant="secondary"
          onClick={() => {
            setError(null)
            addMut.mutate()
          }}
          disabled={addMut.isPending || q.isLoading}
        >
          {t('multi_iban.add')}
        </Button>
      </div>

      {error ? <ErrorBanner message={error} /> : null}

      {q.isLoading ? <AccountSkeleton /> : null}

      {!q.isLoading && accounts.length === 0 ? (
        <p className="text-text-muted">{t('dashboard.no_account')}</p>
      ) : null}

      {accounts.map((account: AccountDto) => (
        <Card key={account.id}>
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-text-muted">{t('dashboard.available_balance')}</p>
              <Money
                className="text-2xl"
                amountCents={account.available_balance_cents}
                currency={account.currency}
                locale={i18n.language}
              />
            </div>
            {account.is_preferred ? (
              <span className="rounded-full border border-border px-2 py-1 text-xs text-text-muted">
                {t('multi_iban.preferred')}
              </span>
            ) : null}
          </div>

          <dl className="mt-4 grid grid-cols-3 gap-y-2 text-sm">
            <dt className="text-text-muted">{t('account.iban')}</dt>
            <dd className="col-span-2 break-all font-mono">{account.iban_pretty}</dd>
            <dt className="text-text-muted">{t('account.bic')}</dt>
            <dd className="col-span-2 font-mono">{account.bic}</dd>
            <dt className="text-text-muted">{t('account.status')}</dt>
            <dd className="col-span-2">
              {t(`account.status_${account.status}`, { defaultValue: account.status })}
            </dd>
          </dl>

          <div className="mt-4 flex gap-2">
            <Button
              variant="secondary"
              disabled={!account.iban}
              onClick={() => account.iban && void navigator.clipboard.writeText(account.iban)}
            >
              {t('account.copy_iban')}
            </Button>
            {!account.is_preferred ? (
              <Button
                variant="tertiary"
                onClick={() => {
                  setError(null)
                  preferMut.mutate(account.id)
                }}
                disabled={preferMut.isPending}
              >
                {t('multi_iban.set_preferred')}
              </Button>
            ) : null}
          </div>
        </Card>
      ))}
    </div>
  )
}
